import React, { Component } from "react";
import { Row, Col, Input, Label, Form, Button, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { toast } from "react-toastify";
import { updateGlobalTargetRates } from "../../services/globalStreamConfigService";
import cloneDeep from "../../utils/parseUtils";

export const TARGET_RATE_SCOPES = [
  { key: "standard", label: "Standard" },
  { key: "simulation", label: "Simulation" },
];

const DEFAULT_TARGET_RATE = 1;
// Both bounds are exclusive, matching the validation of /globalstreamconfigs/.
const MIN_TARGET_RATE = 0;
const MAX_TARGET_RATE = 3600;

function readRate(targetRates, gesture) {
  const rate = targetRates?.[gesture];
  return typeof rate === "number" ? rate : DEFAULT_TARGET_RATE;
}

class ScopeTargetRatesForm extends Component {
  state = {
    backupTargetRates: null,
    inputBody: DEFAULT_TARGET_RATE,
    inputOther: DEFAULT_TARGET_RATE,
    confirmSave: false,
  };

  componentDidMount() {
    const { targetRates: targetRatesProp } = this.props;
    this.loadTargetRates(targetRatesProp);
  }

  componentDidUpdate(prevProps) {
    const { targetRates: targetRatesProp } = this.props;
    const { targetRates: prevTargetRates } = prevProps;
    if (prevTargetRates !== targetRatesProp) {
      this.loadTargetRates(targetRatesProp);
    }
  }

  onInputChange = (gesture, value) => {
    const rate = value === "" ? "" : parseFloat(value);
    if (gesture === "body") {
      this.setState({ inputBody: rate });
    }
    if (gesture === "other") {
      this.setState({ inputOther: rate });
    }
  };

  reset = () => {
    this.setState((prevState) => {
      const { backupTargetRates } = prevState;
      return {
        inputBody: readRate(backupTargetRates, "body"),
        inputOther: readRate(backupTargetRates, "other"),
      };
    });
  };

  validate = (gestureLabel, value) => {
    const rate = parseFloat(value);
    if (Number.isNaN(rate) || rate <= MIN_TARGET_RATE || rate >= MAX_TARGET_RATE) {
      toast.error(`${gestureLabel} must be strictly between ${MIN_TARGET_RATE} and ${MAX_TARGET_RATE}`);
      return null;
    }
    return rate;
  };

  save = async () => {
    const { inputBody, inputOther } = this.state;
    const { scope, label } = this.props;

    const body = this.validate("Body target rate", inputBody);
    if (body === null) {
      return;
    }
    const other = this.validate("Other target rate", inputOther);
    if (other === null) {
      return;
    }

    const targetRates = { body, other };

    try {
      await updateGlobalTargetRates({ [scope]: targetRates });
      this.setState({
        backupTargetRates: cloneDeep(targetRates),
        inputBody: body,
        inputOther: other,
      });
      this.toggleConfirmSave();
      toast.success(`${label} target rates saved successfully`);
    } catch (error) {
      toast.error(`Error saving ${label.toLowerCase()} target rates`);
      console.error(error);
    }
  };

  toggleConfirmSave = () => {
    this.setState((prevState) => {
      const { confirmSave } = prevState;
      return { confirmSave: !confirmSave };
    });
  };

  loadTargetRates(targetRatesProp) {
    const targetRates = cloneDeep(targetRatesProp || {});
    this.setState({
      backupTargetRates: cloneDeep(targetRates),
      inputBody: readRate(targetRates, "body"),
      inputOther: readRate(targetRates, "other"),
    });
  }

  render() {
    const { backupTargetRates, inputBody, inputOther, confirmSave } = this.state;
    const { scope, label } = this.props;

    if (!backupTargetRates) {
      return null;
    }

    return (
      <Form className="mt-3">
        <Row>
          <Col md="4">
            <h5>{label} target rates</h5>
            <div className="input-group mb-3">
              <Label className="mr-2 mt-2" for={`target-rate-${scope}-body`}>
                Body target rate:
              </Label>
              <Input
                id={`target-rate-${scope}-body`}
                className="form-control border border-dark rounded"
                type="number"
                step="0.1"
                min={MIN_TARGET_RATE}
                max={MAX_TARGET_RATE}
                value={inputBody}
                onChange={(e) => this.onInputChange("body", e.target.value)}
              />
            </div>
            <div className="input-group mb-3">
              <Label className="mr-2 mt-2" for={`target-rate-${scope}-other`}>
                Other target rate:
              </Label>
              <Input
                id={`target-rate-${scope}-other`}
                className="form-control border border-dark rounded"
                type="number"
                step="0.1"
                min={MIN_TARGET_RATE}
                max={MAX_TARGET_RATE}
                value={inputOther}
                onChange={(e) => this.onInputChange("other", e.target.value)}
              />
            </div>
            <div>
              <Button className="float-right m-2" onClick={this.reset}>
                <i className="fas fa-times mr-2" /> Reset
              </Button>
            </div>
            {!confirmSave && (
              <div>
                <Button className="float-right btn-success m-2" onClick={this.toggleConfirmSave}>
                  <i className="fas fa-save mr-2" /> Save
                </Button>
              </div>
            )}
            {confirmSave && (
              <div className="save-confirmation">
                <Button className="float-right m-2" onClick={this.toggleConfirmSave}>
                  <i className="fas fa-times mr-2" /> Cancel
                </Button>
                <Button className="float-right btn-success m-2" onClick={this.save}>
                  <i className="fas fa-save mr-2" /> Save
                </Button>
                <span className="float-right">Are you sure?</span>
              </div>
            )}
          </Col>
        </Row>
      </Form>
    );
  }
}

class GlobalTargetRatesForm extends Component {
  state = {
    activeScope: TARGET_RATE_SCOPES[0].key,
  };

  toggleScope(scope) {
    const { activeScope } = this.state;
    if (activeScope !== scope) {
      this.setState({ activeScope: scope });
    }
  }

  render() {
    const { activeScope } = this.state;
    const { globalTargetRates } = this.props;

    return (
      <>
        <Nav tabs>
          {TARGET_RATE_SCOPES.map(({ key, label }) => (
            <NavItem role="button" key={key}>
              <NavLink
                className={activeScope === key ? "active" : ""}
                onClick={() => {
                  this.toggleScope(key);
                }}
              >
                <p>{label}</p>
              </NavLink>
            </NavItem>
          ))}
        </Nav>
        <TabContent activeTab={activeScope}>
          {TARGET_RATE_SCOPES.map(({ key, label }) => (
            <TabPane key={key} tabId={key}>
              <ScopeTargetRatesForm scope={key} label={label} targetRates={globalTargetRates?.[key]} />
            </TabPane>
          ))}
        </TabContent>
      </>
    );
  }
}

export default GlobalTargetRatesForm;
